import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { OrderStatusFollowOrderCardBlockConfig } from '@front-platform/content-builder';
import { orderStatusFollowOrderCardBlockResolveConfig } from '@front-platform/content-builder';
import { ActivatedRoute } from '@angular/router';
import { skipWhile, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, input } from '@angular/core';

import { DoodOrderModel } from '@store/order/order.model';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
export interface OrderStatusFollowOrderCardBlockRuntimeConfig {}

export type OrderStatusFollowOrderCardBlockConfigInput = Partial<
  Omit<OrderStatusFollowOrderCardBlockConfig, keyof OrderStatusFollowOrderCardBlockRuntimeConfig> &
    OrderStatusFollowOrderCardBlockRuntimeConfig
>;
export type OrderStatusFollowOrderCardBlockResolvedConfig = Omit<
  OrderStatusFollowOrderCardBlockConfig,
  keyof OrderStatusFollowOrderCardBlockRuntimeConfig
> &
  Partial<OrderStatusFollowOrderCardBlockRuntimeConfig>;
export interface OrderStatusFollowOrderCardBlockContract extends ConfigComponentContract<
  OrderStatusFollowOrderCardBlockConfigInput,
  OrderStatusFollowOrderCardBlockResolvedConfig
> {}

@Component({
  selector: 'app-order-status-follow-order-card-block',
  templateUrl: './order-status-follow-order-card-block.component.html',
  standalone: false,
})
export class OrderStatusFollowOrderCardBlockComponent
  implements OnInit, OrderStatusFollowOrderCardBlockContract
{
  readonly config = input<OrderStatusFollowOrderCardBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    OrderStatusFollowOrderCardBlockConfig,
    OrderStatusFollowOrderCardBlockRuntimeConfig
  >(orderStatusFollowOrderCardBlockResolveConfig, () => ({ config: this.config() }));
  private _textConfigOverride: string | undefined | undefined;
  get text(): string | undefined {
    return (
      this._textConfigOverride ?? (this.resolvedConfig()['text'] as unknown as string | undefined)
    );
  }
  set text(value: string | undefined) {
    this._textConfigOverride = value;
  }
  get linkText(): string | undefined {
    return this.resolvedConfig()['linkText'] as unknown as string | undefined;
  }
  get displayOnlyIfTypes(): boolean {
    return this.resolvedConfig()['display_only_if_types'] as unknown as boolean;
  }
  get enabledForTypes(): string[] {
    return this.resolvedConfig()['enabled_for_types'] as unknown as string[];
  }
  get displayOnlyIfPaymentMethods(): boolean {
    return this.resolvedConfig()['display_only_if_payment_methods'] as unknown as boolean;
  }
  get enabledForPaymentMethods(): string[] {
    return this.resolvedConfig()['enabled_for_payment_methods'] as unknown as string[];
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }

  displayCard = false;

  constructor(
    private orderSelector: OrderStoreSelector,
    private readonly translate: TranslateService,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    if (!this.text) {
      this.translate.get('order.follow-order').subscribe(text => (this.text = text));
    }
    const orderId = this.activatedRoute.snapshot.params.orderId;
    this.orderSelector
      .selectOrder(orderId)
      .pipe(
        skipWhile(order => !order),
        take(1),
        tap(order => this.checkDisplayConditions(order)),
      )
      .subscribe();
  }

  private checkDisplayConditions(order?: DoodOrderModel): void {
    this.displayCard = false;
    if (!order) {
      return;
    }

    if (
      this.displayOnlyIfPaymentMethods &&
      !this.enabledForPaymentMethods.includes(order.payment_service)
    ) {
      return;
    }
    if (this.displayOnlyIfTypes && !this.enabledForTypes.includes(order.type)) {
      return;
    }

    this.displayCard = true;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(OrderStatusFollowOrderCardBlockComponent, {
  'keyAliases': { 'link_text': 'linkText' },
});
